import { useEffect } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Colored dot for the live phone location (avoids Leaflet's broken default marker images).
const liveIcon = (stale) =>
  L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${stale ? "#9ca3af" : "#16a34a"};
      box-shadow:0 0 0 6px ${stale ? "rgba(156,163,175,.35)" : "rgba(22,163,74,.3)"};border:2px solid #fff"></div>`,
    iconSize: [18, 18], iconAnchor: [9, 9],
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    if (points.length === 1) map.setView(points[0], 7);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
    return () => clearTimeout(t);
  }, [points, map]);
  return null;
}

export const TripMap = ({ live, height = 420 }) => {
  const planned = live?.trip || {};
  const cities = planned.cities || [];
  const positions = live?.positions || [];
  const last = live?.last_position;
  const stamps = live?.stamps || [];
  const stampedRefs = new Set(stamps.map((s) => s.city_ref).filter(Boolean));

  const plannedLine = [];
  if (planned.origin?.lat != null) plannedLine.push([planned.origin.lat, planned.origin.lon]);
  [...cities].sort((a, b) => (a.order || 0) - (b.order || 0)).forEach((c) => plannedLine.push([c.lat, c.lon]));
  if (planned.destination?.lat != null) plannedLine.push([planned.destination.lat, planned.destination.lon]);

  const actualLine = positions.map((p) => [p.lat, p.lon]);
  const allPts = [...plannedLine, ...actualLine];
  const center = last ? [last.lat, last.lon] : (allPts[0] || [23.6, -102.5]);
  const noRoute = plannedLine.length === 0 && actualLine.length === 0 && !last;

  return (
    <div data-testid="trip-map" style={{ height, borderRadius: 16, overflow: "hidden", position: "relative" }}>
      {noRoute && (
        <div data-testid="trip-map-empty" style={{ position: "absolute", inset: 0, zIndex: 500 }}
          className="flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto bg-white/95 backdrop-blur rounded-xl border border-sand-200 shadow-lg px-5 py-4 max-w-sm text-center">
            <div className="font-medium text-charcoal-800 mb-1">No route to show yet</div>
            <p className="text-xs text-charcoal-500">This trip has no planned cities and no GPS fixes yet, so there's nothing to plot. Add the route below (or pick a template), then start the trip phone to see live movement.</p>
          </div>
        </div>
      )}
      <MapContainer center={center} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds points={allPts} />
        {plannedLine.length > 1 && (
          <Polyline positions={plannedLine} pathOptions={{ color: "#c2834a", weight: 3, dashArray: "8 10", opacity: 0.8 }} />
        )}
        {actualLine.length > 1 && (
          <Polyline positions={actualLine} pathOptions={{ color: "#16a34a", weight: 4, opacity: 0.9 }} />
        )}
        {cities.map((c) => {
          const stamped = stampedRefs.has(c.id);
          const border = c.kind === "border";
          return (
            <CircleMarker key={c.id} center={[c.lat, c.lon]} radius={border ? 9 : 7}
              pathOptions={{
                color: border ? "#b91c1c" : (stamped ? "#16a34a" : "#9ca3af"),
                fillColor: stamped ? (border ? "#b91c1c" : "#16a34a") : "#fff",
                fillOpacity: stamped ? 0.9 : 1, weight: 2,
              }}>
              <Tooltip>{c.name}{border ? " (crossing)" : ""}{stamped ? " ✓" : " · upcoming"}</Tooltip>
            </CircleMarker>
          );
        })}
        {last && (
          <Marker position={[last.lat, last.lon]} icon={liveIcon(live?.stale)}>
            <Popup>{live?.stale ? "Last known location" : "Live location"}</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default TripMap;
